import { AxiosInstance, AxiosResponse } from 'axios';
import { OdooOrderResponse, OdooOrderListResponse, TenantServicesResponse } from './types';

/**
 * List Odoo orders with pagination
 * @param api - Axios instance with auth
 * @param options - Query options
 */
export function listOdooOrders(
  api: AxiosInstance,
  options?: {
    limit?: number;
    offset?: number;
    state?: 'draft' | 'sent' | 'sale' | 'done' | 'cancel';
    search?: string;
  }
): Promise<AxiosResponse<OdooOrderListResponse>> {
  return api.get('/odoo/orders', { params: options });
}

/**
 * Fetch Odoo order by ID
 * @param api - Axios instance with auth
 * @param orderId - Odoo sale order ID
 * @param format - Response format ('transformed' | 'raw')
 */
export function fetchOdooOrder(
  api: AxiosInstance,
  orderId: number,
  format: 'transformed' | 'raw' = 'transformed'
): Promise<AxiosResponse<OdooOrderResponse>> {
  return api.get(`/odoo/orders/${orderId}`, {
    params: { format }
  });
}

/**
 * Confirm an Odoo order (draft -> sale)
 * @param api - Axios instance with auth
 * @param orderId - Odoo sale order ID
 */
export function confirmOdooOrder(
  api: AxiosInstance,
  orderId: number
): Promise<AxiosResponse<OdooOrderResponse>> {
  return api.post(`/odoo/orders/${orderId}/confirm`);
}

/**
 * Check Odoo connection health
 * @param api - Axios instance with auth
 */
export function checkOdooHealth(
  api: AxiosInstance
): Promise<AxiosResponse<{ success: boolean; message: string }>> {
  return api.get('/odoo/health');
}

/**
 * Fetch existing KontrolEdge services for a tenant.
 * Used to assign child services from the current order to parent services
 * created in previous orders for the same tenant.
 *
 * @param api - Axios instance with auth
 * @param tenantId - DB tenant ID
 * @param excludeOrderId - Odoo order ID to exclude (current order)
 * @param types - Service types to fetch (default: port,internet,cloud)
 */
export function fetchTenantServices(
  api: AxiosInstance,
  tenantId: number,
  excludeOrderId?: number,
  types?: string[]
): Promise<AxiosResponse<TenantServicesResponse>> {
  return api.get(`/presales/tenant-services/${tenantId}`, {
    params: {
      excludeOrderId,
      types: types?.join(',')
    }
  });
}

